import {
  WSActions,
  WSCard,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IPayoutSettingsResponse,
  PayoutDestinationType,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import { IAccount } from "@wingspanhq/users/dist/lib/interfaces";
import { useWSMutation } from "@ws-react-query";
import { useEffect, useMemo, useState } from "react";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { WSQueries } from "../../../../query/WSQuery";
import { useQueryInternalAccounts } from "../../../../query/banking/queries/useQueryInternalAccounts";
import { useUserId } from "../../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useQueryCustomerEntity } from "../../../../query/onboarding/queries/useQueryCustomerEntity";
import { useQueryPaymentsCardsDebit } from "../../../../query/paymentCards/queries/useQueryPaymentsCardsDebit";
import { QUERY_PAYOUT_SETTINGS } from "../../../../query/payments/keys";
import {
  useQueryPayoutSettings,
  useQueryPayoutSettingsDebitCards
} from "../../../../query/payments/queries";
import {
  useQueryAccounts,
  useQueryMemberProfile
} from "../../../../query/users/queries";
import { paymentsService } from "../../../../services/payments";
import { selectorAccountIPReady } from "../../../../shared/selectors/selectorAccountIPReady";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorIsAccountInternational } from "../../../../shared/selectors/selectorIsAccountInternational";
import { selectorPaymentCardPayoutReady } from "../../../../shared/selectors/selectorPaymentCardPayoutReady";
import { selectorUseInternationalInstantPayouts } from "../../../../shared/selectors/selectorUseInternationalInstantPayouts";
import { selectorUseInternationalPayouts } from "../../../../shared/selectors/selectorUseInternationalPayouts";
import { WSServiceError } from "../../../../utils/serviceHelper";
import { selectorIsPlatformBalanceAccount } from "../../../PlatformBalance/selectors/selectorIsPlatformBalanceAccount";
import { List } from "../ManageMethods/List";
import { MethodValue } from "../ManageMethods/types";
import { useModalConfirmPayoutMethodChange } from "../ModalConfirmPayoutMethodChange";
import { useDefaultPayoutMethod } from "./DefaultPayoutMethod";
import { useModalInfo } from "./ModalInfo";
import { useInstantPayoutFee } from "../../hooks/useInstantPayoutFee";
import { selectorAccountPayoutReady } from "../../selectors/selectorAccountPayoutReady";
import { PayoutMethodType } from "./FlowAddPayoutMethod";

type Props = {
  onSaved: () => void;
  onAddNew: () => void;
  error?: WSServiceError;
  availableUsMethods?: PayoutMethodType[];
  availableInternationalMethods?: PayoutMethodType[];
};

export const ManagePayoutMethod: React.FC<Props> = props => {
  const userId = useUserId();
  const queryInternalAccounts = useQueryInternalAccounts();
  const queryAccounts = useQueryAccounts();
  const queryPayoutSettingsDebitCards =
    useQueryPayoutSettingsDebitCards(userId);
  const queryPaymentCards = useQueryPaymentsCardsDebit();
  const queryMember = useQueryMemberProfile(userId);
  const queryCustomerEntity = useQueryCustomerEntity();
  const queryPayoutSettings = useQueryPayoutSettings(userId);
  const queryFeatureFlags = useFeatureFlags();

  const [value, setValue] = useState<MethodValue | null | undefined>();

  const defaultPayoutMethod = useDefaultPayoutMethod();
  useEffect(() => {
    if (defaultPayoutMethod) {
      setValue(defaultPayoutMethod);
    }
  }, [defaultPayoutMethod]);

  const isInternational = queryMember.data
    ? selectorUseInternationalPayouts(
        queryMember.data,
        queryCustomerEntity.data
      )
    : false;

  const isUsMethodAllowed = (method: PayoutMethodType) =>
    !props.availableUsMethods || props.availableUsMethods.includes(method);

  const isInternationalMethodAllowed = (method: PayoutMethodType) =>
    !props.availableInternationalMethods ||
    props.availableInternationalMethods.includes(method);

  const restrictions: {
    showInstantPayoutMethods: boolean;
    isAccountAllowed: (account: IAccount) => boolean;
    isPlatformBalanceAllowed: boolean;
  } = isInternational
    ? {
        showInstantPayoutMethods:
          !!queryFeatureFlags.data?.internationalIP &&
          !!queryMember.data &&
          selectorUseInternationalInstantPayouts(
            queryMember.data,
            queryCustomerEntity.data,
            queryPayoutSettings.data
          ) &&
          isInternationalMethodAllowed("international"),
        isAccountAllowed: account =>
          selectorIsAccountInternational(account)
            ? isInternationalMethodAllowed("international")
            : isInternationalMethodAllowed("standard"),
        isPlatformBalanceAllowed:
          isInternationalMethodAllowed("platform-balance")
      }
    : {
        showInstantPayoutMethods: isUsMethodAllowed("instant"),
        isAccountAllowed: () => true,
        isPlatformBalanceAllowed: true
      };
  const ipFee = useInstantPayoutFee();
  const modalInfo = useModalInfo();

  const [mutate, meta] = useWSMutation<
    IPayoutSettingsResponse | void,
    WSServiceError,
    MethodValue | null
  >(
    async data => {
      if (!data) {
        return;
      }

      if (!data.category) {
        throw new Error("No payout preference specified");
      }

      const apPayoutInternalAccount = queryInternalAccounts.data?.find(
        internalAccount =>
          selectorIsPlatformBalanceAccount(
            internalAccount,
            queryPayoutSettings.data
          )
      );

      if (
        !(
          data.category === PayoutPreferences.Standard ||
          data.category === PayoutPreferences.Instant
        )
      ) {
        throw new Error("Invalid payout preference");
      }

      if (data.category === PayoutPreferences.Instant) {
        let destinationType: PayoutDestinationType;

        if (data.type === "InternalAccount") {
          if (apPayoutInternalAccount) {
            return await paymentsService.payoutSettings.update(userId, {
              payoutPreferences: PayoutPreferences.Instant,
              instant: {
                destinationType: "InternalAccount" as PayoutDestinationType,
                destinationId: apPayoutInternalAccount.internalAccountId
              }
            });
          }

          return await paymentsService.payoutSettings.update(userId, {
            payoutPreferences: PayoutPreferences.Instant,
            instant: null
          });
        }

        switch (data.type) {
          case "Account":
            destinationType = PayoutDestinationType.Account;
            break;
          case "CheckbookCard":
            destinationType = PayoutDestinationType.Card;
            break;
          case "PaymentCard":
            destinationType = PayoutDestinationType.PaymentCard;
            break;
          default:
            throw new Error(
              `${data.type} is not a valid payout destination type for Instant payout method`
            );
        }

        return await paymentsService.payoutSettings.update(userId, {
          payoutPreferences: PayoutPreferences.Instant,
          instant: {
            destinationType,
            destinationId: data.id
          }
        });
      }

      if (data.type === "Account") {
        return await paymentsService.payoutSettings.update(userId, {
          payoutPreferences: PayoutPreferences.Standard,
          standard: {
            destinationType: PayoutDestinationType.Account,
            destinationId: data.id
          }
        });
      } else if (data.type === "InternalAccount") {
        if (apPayoutInternalAccount) {
          return await paymentsService.payoutSettings.update(userId, {
            payoutPreferences: PayoutPreferences.Standard,
            standard: {
              destinationType: "InternalAccount" as PayoutDestinationType,
              destinationId: apPayoutInternalAccount.internalAccountId
            }
          });
        }

        return await paymentsService.payoutSettings.update(userId, {
          payoutPreferences: PayoutPreferences.Standard,
          standard: null
        });
      } else {
        throw new Error(
          `${data.type} is not a valid payout destination type for Standard payout method`
        );
      }
    },
    {
      onSuccess: props.onSaved,
      dependencies: [QUERY_PAYOUT_SETTINGS]
    }
  );

  const modalConfirmPayoutMethodChange = useModalConfirmPayoutMethodChange();

  const isSameMethodSelected = useMemo(
    () =>
      value?.id === defaultPayoutMethod?.id &&
      value?.category === defaultPayoutMethod?.category,
    [value, defaultPayoutMethod]
  );

  return (
    <WSQueries
      queries={{
        queryAccounts,
        queryPayoutSettingsDebitCards,
        queryPaymentCards,
        queryInternalAccounts,
        queryPayoutSettings
      }}
    >
      {({
        queryAccounts: { data: accounts },
        queryPayoutSettingsDebitCards: { data: debitCards },
        queryPaymentCards: { data: paymentCards },
        queryInternalAccounts: { data: internalAccounts },
        queryPayoutSettings: { data: payoutSettings }
      }) => {
        const allowedInternalAccounts = internalAccounts.filter(
          internalAccount =>
            selectorActiveBankingAccount(internalAccount) ||
            (selectorIsPlatformBalanceAccount(
              internalAccount,
              payoutSettings
            ) &&
              restrictions.isPlatformBalanceAllowed)
        );

        return (
          <WSList gap="XL">
            {/* Surface the upstream error (e.g. failed save the user came back from) */}
            <WSErrorMessage contextKey="PayoutSettings" error={props.error} />

            <WSElement>
              <WSText weight="medium" mb="M">
                Standard payout methods
              </WSText>

              <List
                data={{
                  accounts: accounts.filter(
                    account =>
                      selectorAccountPayoutReady(account) &&
                      restrictions.isAccountAllowed(account)
                  ),
                  internalAccounts: allowedInternalAccounts
                }}
                onChange={setValue}
                value={value}
                category={PayoutPreferences.Standard}
                initialValue={defaultPayoutMethod}
              />

              {restrictions.showInstantPayoutMethods && (
                <>
                  <WSDivider mt="2XL" mb="3XL" />

                  <WSFlexBox gap="S" alignItems="center" mb="M">
                    <WSText weight="medium">
                      Instant payout methods ({ipFee}% fee)
                    </WSText>

                    <WSIcon
                      name="info-circle-fill"
                      color="gray500"
                      size="S"
                      onClick={() => {
                        modalInfo.open();
                      }}
                    />
                  </WSFlexBox>

                  <List
                    data={{
                      accounts: accounts.filter(
                        account =>
                          selectorAccountIPReady(account, isInternational) &&
                          restrictions.isAccountAllowed(account)
                      ),
                      checkbookCards: debitCards,
                      paymentCards: paymentCards.filter(
                        selectorPaymentCardPayoutReady
                      ),
                      internalAccounts: allowedInternalAccounts
                    }}
                    onChange={setValue}
                    value={value}
                    category={PayoutPreferences.Instant}
                    initialValue={defaultPayoutMethod}
                  />
                </>
              )}
            </WSElement>

            <WSCard
              onClick={props.onAddNew}
              actions={{
                alignment: "left",
                buttons: [
                  {
                    icon: "plus-circle",
                    kind: "Link",
                    label: "Add new payout method"
                  }
                ]
              }}
            />

            <WSErrorMessage contextKey="PayoutSettings" error={meta.error} />

            <WSActions
              alignment="fill"
              buttons={[
                {
                  label: "Save changes",
                  onClick: () => {
                    if (!defaultPayoutMethod) {
                      mutate(value);
                      return;
                    }
                    modalConfirmPayoutMethodChange.open({
                      onSave: () => {
                        mutate(value);
                      },
                      previousMethod: defaultPayoutMethod,
                      newMethod: value!
                    });
                  },
                  loading: meta.isLoading,
                  disabled: isSameMethodSelected
                }
              ]}
            />
          </WSList>
        );
      }}
    </WSQueries>
  );
};
